import { useMutation } from '@tanstack/react-query';
import { uniq } from 'lodash';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import { AreaType, useAreas } from 'services/assist-api/entities/area/areas';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { prepareCustomerArea } from './customers-areas.prepare';

export type BaseCustomerArea = API_JSON & {
  enabled: boolean;
  private: boolean;
  customer: API_JSON;
  area: API_JSON;
};

const groups = ['customersareas-customer', 'customersareas-area', 'enabled'];
/**
 * Fetch customers-areas
 * @param params can be used to customise/limit the customers-areas retrieved
 * @returns an array of customers-areas
 */
export const getCustomerAreas: GetMany<BaseCustomerArea> = async (params) =>
  (await ASSIST_API.get('customers-areas', { params: requestParams({ groups }, params) })).data;

/**
 * Hook implementation to fetch customers-areas
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns customers-areas matching request
 */
export const useCustomerAreas: AssistQuery.UseMany<BaseCustomerArea> = (params, options) =>
  useQueryAssist(['customers-areas', params], () => getCustomerAreas(params), options);

/**
 * Given an area Id and customer Id, fetches and checks if the area has an active subscription for the given customer
 *  - only runs once area and customer is provided
 *  - returns an array containing any matching areas that are populated (should only be one) or empty if there are none
 *  - caches data
 * @param area areaId
 * @param params optional parameters to change requested data
 */
export const useActiveCustomerAreas: AssistQuery.UseOne<
  BaseCustomerArea[],
  { area: number; customer: number }
> = ({ area, customer }, params) =>
  useQueryAssist(
    ['customers-areas', { ...params, filters: { area, customer } }],
    () => getCustomerAreas({ ...params, filters: { area, customer, enabled: true } }),
    {
      enabled: !!area && !!customer
    }
  );

type PostCustomerAreasVariables = {
  customerId: number;
  areas: number[];
};

/**
 * Creates methods to send POST requests to the api
 *
 * mutate call will require a customer ID and area ID
 *
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePostCustomerAreas: AssistQuery.UseMutations<
  undefined,
  PostCustomerAreasVariables
> = (options) =>
  useMutation(
    ({ customerId, areas }: PostCustomerAreasVariables) =>
      Promise.allSettled(
        areas.map((id) =>
          ASSIST_API.post('customers-areas', prepareCustomerArea(customerId, id), {
            params: requestParams({ groups })
          })
        )
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['customers-areas']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

export const useIsActiveOrganisationSubscribingAssessmentAreas = (): boolean => {
  const customer = useAuthCustomer();
  const customerAreas = useCustomerAreas({ filters: { customer: customer?.id } }).data?.json;
  const { data: areas } = useAreas();

  const areaIds = customerAreas?.map((organisationArea) => organisationArea.area.id);
  const types = areaIds
    ?.map((areaId) => areas?.json.find((area) => area.id === areaId)?.type)
    .filter((obj) => !!obj);
  const areaTypes = uniq(types);

  return areaTypes?.includes(AreaType.Assessment);
};
